import { h } from "preact";
import { useLayoutEffect, useMemo, useRef, useState } from "preact/hooks";
import { QuantityMinus, QuantityPlus } from "../../helpers/icons";
import richTextToHtml, {
  escapeHtml,
} from "../../helpers/richText/richTextToHtml";

/**
 * A metaobject field comes back as { type, value }. Liquid's `| metafield_tag`
 * renders rich text server-side; the Storefront API hands over the raw document.
 */
const fieldToHtml = (field) => {
  if (!field?.value) return "";

  if (field.type === "rich_text_field") return richTextToHtml(field.value);

  return field.value
    .split("\n")
    .filter((line) => line.trim())
    .map((line) => `<p>${escapeHtml(line)}</p>`)
    .join("");
};

const materialDetailsOf = (details) => details?.variants?.[0]?.materialDetails;

/**
 * Body copy for one swatch: description and material details clamped to a few
 * lines behind a Read more toggle, with care instructions as a nested
 * accordion. The parent keys this on the swatch so switching chips collapses it.
 */
export default function SwatchDetailsBody({ details, loading }) {
  const [expanded, setExpanded] = useState(false);
  const [truncated, setTruncated] = useState(false);
  const ref = useRef(null);

  const copyHtml = useMemo(() => {
    const materialDetails = materialDetailsOf(details);

    return [details?.descriptionHtml, fieldToHtml(materialDetails?.details)]
      .filter(Boolean)
      .join("");
  }, [details]);

  const careHtml = useMemo(
    () => fieldToHtml(materialDetailsOf(details)?.careInstructions),
    [details],
  );

  useLayoutEffect(() => {
    const copy = ref.current;

    if (!copy) return;

    // Only the collapsed box can be measured against its own clamp, so once
    // expanded the last verdict stands and the toggle stays put.
    const measure = () => {
      if (expanded) return;

      setTruncated(copy.scrollHeight > copy.clientHeight + 1);
    };

    measure();

    const observer = new ResizeObserver(measure);

    observer.observe(copy);

    return () => observer.disconnect();
  }, [copyHtml, expanded]);

  if (!copyHtml && !careHtml) {
    return loading ? (
      <div className="swatch-details-drawer__body-skeleton"></div>
    ) : null;
  }

  const strings = window.localeStrings.swatch_details;
  // Care sits with the copy the toggle reveals — except when there is nothing to
  // reveal, where holding it back would hide it for good.
  const showCare = careHtml && (expanded || !truncated);

  return (
    <div className="swatch-details-drawer__body">
      <div
        ref={ref}
        className={`swatch-details-drawer__body-copy rte--simple ${
          expanded ? "swatch-details-drawer__body-copy--expanded" : ""
        }`}
        dangerouslySetInnerHTML={{ __html: copyHtml }}
      ></div>
      {showCare && (
        <conditional-details>
          <details className="swatch-details-drawer__care">
            <summary className="swatch-details-drawer__care-summary">
              <span className="link link--x-small link--black">
                {window.localeStrings.product.care_instructions}
              </span>
              <span className="swatch-details-drawer__care-icon">
                <QuantityPlus />
                <QuantityMinus />
              </span>
            </summary>
            <div className="swatch-details-drawer__care-content" data-content>
              <div
                className="swatch-details-drawer__care-content-inner rte--simple"
                dangerouslySetInnerHTML={{ __html: careHtml }}
              ></div>
            </div>
          </details>
        </conditional-details>
      )}
      {truncated && (
        <button
          type="button"
          className="link link--x-small link--black swatch-details-drawer__body-toggle"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? strings.read_less : strings.read_more}
        </button>
      )}
    </div>
  );
}
